import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { formatViews } from "@/utils/catalog";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影分类", to: "/category" },
  { label: "影评资讯", to: "/reviews" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [focused, setFocused] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setFocused(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen || searchOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, searchOpen]);

  const trimmed = keyword.trim();
  const suggestions = trimmed
    ? movies
        .filter((movie) =>
          [movie.title, movie.enTitle, ...movie.genres].join(" ").toLowerCase().includes(trimmed.toLowerCase()),
        )
        .slice(0, 5)
    : [];

  const submitSearch = () => {
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setFocused(false);
    setSearchOpen(false);
    setKeyword("");
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-background-200/80 bg-background-50">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-[68px] items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-3 whitespace-nowrap">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-600 text-background-50">
                <i className="ri-movie-2-line text-xl leading-none" />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="font-heading text-[19px] font-semibold tracking-wide text-foreground-950">
                  苍井空电影
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-foreground-400">
                  Sora Aoi Film Archive
                </span>
              </span>
            </Link>

            <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `relative px-4 py-2 text-sm transition-colors ${
                      isActive
                        ? "text-primary-700"
                        : "text-foreground-600 hover:text-primary-600"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      <span
                        className={`absolute bottom-0 left-1/2 h-[2px] -translate-x-1/2 rounded-full bg-primary-500 transition-all ${
                          isActive ? "w-5" : "w-0"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <div ref={boxRef} className="relative hidden md:block">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  submitSearch();
                }}
                className="flex items-center gap-2 rounded-full border border-background-300/80 bg-background-100/80 px-3 py-2 transition-colors focus-within:border-primary-400"
              >
                <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-sm leading-none" />
                </span>
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  onFocus={() => setFocused(true)}
                  placeholder="搜索电影或影评"
                  aria-label="搜索电影或影评"
                  className="w-36 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none xl:w-48"
                />
                <button
                  type="submit"
                  className="whitespace-nowrap rounded-full bg-primary-600 px-3 py-1 text-xs text-background-50 transition-colors hover:bg-primary-700"
                >
                  搜索
                </button>
              </form>

              {focused && trimmed ? (
                <div className="absolute right-0 top-[calc(100%+10px)] w-[340px] overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  <p className="border-b border-background-200/80 px-4 py-2 text-xs text-foreground-500">
                    找到 {suggestions.length} 个相关作品
                  </p>
                  {suggestions.length === 0 ? (
                    <p className="px-4 py-4 text-sm text-foreground-500">没有匹配的作品，试试其他关键词</p>
                  ) : (
                    suggestions.map((movie) => (
                      <Link
                        key={movie.id}
                        to={`/detail/${movie.id}`}
                        onClick={() => {
                          setFocused(false);
                          setKeyword("");
                        }}
                        className="flex items-center gap-3 px-3 py-2 transition-colors hover:bg-background-100"
                      >
                        <span className="block h-14 w-10 shrink-0 overflow-hidden rounded">
                          <img src={movie.cover} alt={`${movie.title}封面`} className="h-full w-full object-top object-cover" />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-foreground-900">{movie.title}</span>
                          <span className="block text-xs text-foreground-500">
                            {movie.year} · {movie.genres.join(" / ")}
                          </span>
                        </span>
                      </Link>
                    ))
                  )}
                  <button
                    onClick={submitSearch}
                    className="w-full border-t border-background-200/80 px-4 py-2 text-left text-xs text-primary-600 hover:bg-primary-50"
                  >
                    查看「{trimmed}」的全部搜索结果
                  </button>
                </div>
              ) : null}
            </div>

            <button
              onClick={() => setSearchOpen(true)}
              aria-label="打开搜索"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300/80 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden"
            >
              <i className="ri-search-line text-lg leading-none" />
            </button>

            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300/80 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 lg:hidden"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl leading-none`} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen ? (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <nav className="flex flex-col px-4 py-3" aria-label="移动端导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md px-3 py-3 text-[15px] ${
                    isActive ? "bg-primary-50 text-primary-700" : "text-foreground-700"
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400" />
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-background-200 px-4 py-3 text-xs text-foreground-500">
            共收录 {movies.length} 部作品 · {formatViews(1284000)} 次浏览
          </div>
        </div>
      ) : null}

      {searchOpen ? (
        <div className="fixed inset-0 z-50 flex flex-col bg-background-50 md:hidden">
          <div className="flex items-center gap-3 border-b border-background-200 px-4 py-3">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch();
              }}
              className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2"
            >
              <i className="ri-search-line text-foreground-400" />
              <input
                autoFocus
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="输入电影名称或影评关键词"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </form>
            <button
              onClick={() => setSearchOpen(false)}
              className="whitespace-nowrap text-sm text-foreground-600"
            >
              取消
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {trimmed ? (
              suggestions.length ? (
                <div className="space-y-2">
                  {suggestions.map((movie) => (
                    <Link
                      key={movie.id}
                      to={`/detail/${movie.id}`}
                      className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-3"
                    >
                      <span className="block h-16 w-11 shrink-0 overflow-hidden rounded">
                        <img src={movie.cover} alt={`${movie.title}封面`} className="h-full w-full object-top object-cover" />
                      </span>
                      <span>
                        <span className="block text-sm text-foreground-900">{movie.title}</span>
                        <span className="block text-xs text-foreground-500">
                          {movie.year} · {movie.genres.join(" / ")}
                        </span>
                      </span>
                    </Link>
                  ))}
                  <button
                    onClick={submitSearch}
                    className="w-full rounded-md bg-primary-600 py-3 text-sm text-background-50"
                  >
                    查看全部搜索结果
                  </button>
                </div>
              ) : (
                <p className="mt-8 text-center text-sm text-foreground-500">
                  没有找到相关内容，试试其他关键词
                </p>
              )
            ) : (
              <div>
                <p className="mb-3 text-xs uppercase tracking-[0.2em] text-foreground-400">热门搜索</p>
                <div className="flex flex-wrap gap-2">
                  {["东京暮色", "白色回声", "纪录", "悬疑", "影评"].map((word) => (
                    <button
                      key={word}
                      onClick={() => setKeyword(word)}
                      className="rounded-full border border-background-300 px-3 py-1.5 text-sm text-foreground-600"
                    >
                      {word}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </header>
  );
}